import { Box, Button, FlexBox, Icon, TextArea, TextInputWithButtonInset, Typography } from '@vp/swan';
import React, { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { EMOJIS } from '../../../constants/general';
import { CAContext } from '../../../contexts/CAContext';
import { ChatbotContext } from '../../../contexts/ChatbotContext';
import { useCustomTracking } from '../../../hooks/custom-hooks/useTracking';
import PrivacyCookiePolicy from '../../PrivacyCookiePolicy/PrivacyCookiePolicy';
import './ChatFooter.styles.scss';
import ButtonGroup from '../../AdvanceMessagingComponent/ButtonGroup/ButtonGroup';
import { MiawChatContext } from '../../../contexts/MiawChatContext';
import { useMIAWUploadFile } from '../../../hooks/miaw/useMIAWUploadFile';
import { isMobile } from '../../../utils/mobile-utils';
import UploadAttachmentsPreview from '../../UploadAttachments/UploadAttachmentsPreview';
import UploadAttachmentButton from '../../UploadAttachments/UploadAttachmentButton';
import { IMessagePayload } from '../../../interfaces/component-interfaces';
import { useMIAWTypingIndicator } from '../../../hooks/miaw/useMIAWTypingIndicator';
import { BottomFixed } from 'react-bottom-fixed';
import { getBrowserName } from '../../../utils/app-utils';

import { useChatFooterWebSocket } from '../../../hooks/chatbot/useChatFooterWebSocket';

export interface IChatFooterProps {
  onSendMessage?: (message: string) => void;
  disableInput?: boolean; // Optional prop to disable input
  isKeyboardVisible?: boolean; // Optional prop to indicate if keyboard is visible
}

const ChatFooter = forwardRef<HTMLDivElement, IChatFooterProps>(({ onSendMessage, disableInput }, ref) => {
  const {
    chatState: { chatSession, enableChatbot, isPrivacyPolicyAccepted },
    setChatState,
  } = useContext(CAContext);

  const {
    state: { conversationId, isMIAWApiLoading, endChatData: miawEndChatData, isMIAWEndChatError, isRoutingError, isChatEndedByAgent },
    setState: setMiawState,
  } = useContext(MiawChatContext);

  const {
    state: {
      startChatbotSession,
      showHelperButton,
      isSendMessageLoading,
      endChatData: chatbotEndChatData,
      stopChatbotMessage,
      isChatbotEndError,
      isSessionExpired,
    },
    setState: setChatbotState,
  } = useContext(ChatbotContext);
  const { sendTrackingEvent } = useCustomTracking();

  const { trigger: typingIndicator } = useMIAWTypingIndicator();

  const [message, setMessage] = useState<string>('');

  const [isTyping, setIsTyping] = useState(false);
  const typingTimeout = useRef<number | null>(null);
  const TYPING_DELAY = 5000; // 5s after last keypress = stopped typing

  /** Is session is active, privacy policy will be accepted by default */
  useEffect(() => {
    if (chatSession === 'miawChatSession' || chatSession === 'chatbotSession') {
      setChatState((prevState) => ({
        ...prevState,
        isPrivacyPolicyAccepted: true,
      }));
    } else {
      setChatState((prevState) => ({
        ...prevState,
        isPrivacyPolicyAccepted: false,
      }));
    }
  }, [chatSession]);

  const [showEmojiPicker, setShowEmojiPicker] = React.useState<boolean>(false);
  const [prevIsLoading, setPrevIsLoading] = React.useState<boolean>(false);
  const [selectedFilePayload, setSelectedFilePayload] = React.useState<IMessagePayload[]>([]);
  const { data: uploadData, loading: uploadLoading, trigger: triggerUpload } = useMIAWUploadFile();

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const webSocket = useChatFooterWebSocket({ conversationId: conversationId || '', isrequestConnection: true });
  const sendMessageChange = webSocket?.handleTyping;

  const isIOSMobile = isMobile() && getBrowserName() === 'Safari';

  const handleEmojiClick = (emoji: string) => {
    setMessage((prev) => prev + emoji);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSendMessage();
    }
  };
  const handleSendMessage = () => {
    sendMessageChange?.('');
    if (selectedFilePayload?.length) {
      const trimmedMessage = message.trim();

      if (trimmedMessage && selectedFilePayload.length === 1) {
        triggerUpload({ payload: selectedFilePayload, message: trimmedMessage });
      } else {
        triggerUpload({ payload: selectedFilePayload, message: '' });
        if (trimmedMessage) {
          onSendMessage?.(trimmedMessage);
        }
      }

      if (trimmedMessage) {
        setMessage('');
      }
    } else if (message.trim() !== '') {
      onSendMessage?.(message);
      setMessage('');
    }
  };
  const handleChangeMessage = (e) => {
    setMessage(e.target?.value);
    if (chatSession !== 'miawChatSession') return;
    sendMessageChange?.(e.target?.value);
    if (!isTyping) {
      setIsTyping(true);
      typingIndicator('TypingStartedIndicator');
    }

    // Reset timer on every keystroke
    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      setIsTyping(false);
      typingIndicator('TypingStoppedIndicator');
    }, TYPING_DELAY);
  };

  useEffect(() => {
    return () => {
      if (typingTimeout?.current) clearTimeout(typingTimeout.current);
    };
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (!message) {
      textarea.style.height = 'auto';
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 69) + 'px';

    // Add focus/blur handlers to help with keyboard visibility on mobile
    const handleFocus = () => {
      // When input is focused on mobile, the parent component will handle showing keyboard
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        // This will help maintain focus when keyboard is visible
        const scrollToBottom = setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 300);
        return () => clearTimeout(scrollToBottom);
      }
    };

    textarea.addEventListener('focus', handleFocus);

    return () => {
      textarea.removeEventListener('focus', handleFocus);
    };
  }, [message]);

  /** Start chatbot session once privacy policy are accepted */
  useEffect(() => {
    if (isPrivacyPolicyAccepted) {
      if (chatSession === 'initial' && enableChatbot) {
        startChatbotSession?.();
      }
    }
  }, [isPrivacyPolicyAccepted, chatSession, enableChatbot]);

  // Auto-focus input box when isSendMessageLoading becomes false
  useEffect(() => {
    if (isMobile()) return; // Skip auto-focus on mobile to avoid keyboard issues
    const isCurrentlyLoading = isSendMessageLoading || isMIAWApiLoading || false;

    // Focus only when the loading state changes from true to false
    if (prevIsLoading && !isCurrentlyLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
    // Update previous loading state
    setPrevIsLoading(isCurrentlyLoading);
  }, [isSendMessageLoading, isMIAWApiLoading, prevIsLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
        // Don't do anything here, let the button's onClick handle it
        return;
      }
      if (wrapperRef.current && !wrapperRef.current?.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document?.addEventListener('mousedown', handleClickOutside);
    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const errorFileName: string[] = [];
    if (uploadData && uploadData.length) {
      setSelectedFilePayload([]);
      if (message.trim() !== '') {
        onSendMessage?.(message);
        setMessage('');
      }
      console.log(uploadData, selectedFilePayload, 'only response');
      uploadData.map((res) => {
        if (res.status == 415 && res.fileName) {
          errorFileName.push(res.fileName);
        }
      });

      if (errorFileName.length) {
        const formattedNames = errorFileName.map((name) => {
          return name.length > 10 ? name.slice(0, 10) + '…' : name;
        });
        const message =
          formattedNames.length === 1 ? `${formattedNames[0]} is not a supported file type.` : `${formattedNames.join(', ')} are not a supported file type.`;

        setMiawState((prev) => ({
          ...prev,
          attachmentErrorMessage: message,
          showAttachmentError: true,
        }));

        // setSelectedFilePayload((prev) => prev.filter((file) => errorFileName.includes(file.fileData.name)));
      }
    }
  }, [uploadData]);

  useEffect(() => {
    if (miawEndChatData || chatbotEndChatData || isChatbotEndError || isMIAWEndChatError) {
      setMessage('');
    }
  }, [miawEndChatData, chatbotEndChatData, isChatbotEndError, isMIAWEndChatError]);

  const handlePrivacyPolicyAccept = () => {
    setChatState((prevState) => ({
      ...prevState,
      isPrivacyPolicyAccepted: true,
    }));
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:Privacy', 'chatPrivacyAccepted');
  };
  const onHelperButtonSelect = (option: string) => {
    setChatbotState((prevState) => ({
      ...prevState,
      showHelperButton: false,
    }));
    sendTrackingEvent('Chatbot Clicked', 'Chatbot:SuggestionOptions', `botSuggestionOptionsClicked:${option}`);
  };
  const handleFileChange = (payload: IMessagePayload[]) => {
    if (payload && payload.length > 0) {
      setSelectedFilePayload((prev) => [...prev, ...payload]);
    }
  };

  function handleDeletePreview(fileName: string) {
    setSelectedFilePayload((prev) => prev.filter((file) => file.fileData.name !== fileName));
  }
  if (!isPrivacyPolicyAccepted && chatSession === 'initial' && enableChatbot) {
    return (
      <Box className={`chatWindowFooter`} compactMode id={'chatWindowFooter'}>
        <PrivacyCookiePolicy onPrivacyCookiePolicyAccept={handlePrivacyPolicyAccept} />
      </Box>
    );
  }

  const getFooterBox = () => {
    return (
      <FlexBox style={{ gap: '8px' }}>
        {chatSession === 'miawChatSession' && (
          <Box mr={4}>
            <UploadAttachmentButton
              handleFileChange={handleFileChange}
              attachmentMessage={message}
              disabled={disableInput || isSessionExpired || isRoutingError || isChatEndedByAgent}
            />{' '}
          </Box>
        )}
        <TextInputWithButtonInset compactMode className="chatInputContainer">
          <TextArea
            id="chatInputBox"
            compactMode
            className="chatInput"
            placeholder="Type here"
            value={message}
            onKeyDown={handleKeyDown}
            onChange={handleChangeMessage}
            ref={textareaRef}
            rows={1}
            disabled={disableInput || isSendMessageLoading || isMIAWApiLoading || isSessionExpired || isRoutingError || isChatEndedByAgent}
          />
          <Button
            disabled={disableInput || isSendMessageLoading || isMIAWApiLoading || isSessionExpired || isRoutingError || isChatEndedByAgent}
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            display={{ xs: 'none', sm: 'flex' }}
            ref={buttonRef}
          >
            <Icon iconType="emoji" />
          </Button>
        </TextInputWithButtonInset>
        {isSendMessageLoading || isMIAWApiLoading ? (
          <Button
            skin="tertiary"
            onClick={() => {
              stopChatbotMessage?.();
            }}
            p={4}
          >
            <Icon alt="image" iconType="pause" skin="error" />
          </Button>
        ) : (
          <Button skin="tertiary" onClick={handleSendMessage} p={4} disabled={disableInput || isSessionExpired || isRoutingError || isChatEndedByAgent}>
            <Icon alt="image" iconType="send" />
          </Button>
        )}
      </FlexBox>
    );
  };
  return (
    <>
      {showEmojiPicker && (
        <Box className="emoji-set" ref={wrapperRef} compactMode>
          {EMOJIS.map((emoji, idx) => (
            <Typography key={idx} className="emoji-button" onClick={() => handleEmojiClick(emoji)}>
              {emoji}
            </Typography>
          ))}
        </Box>
      )}
      <FlexBox flexDirection={'column'} ref={ref} style={{ width: '100%' }}>
        {!disableInput && showHelperButton && chatSession === 'chatbotSession' && (
          <FlexBox flexDirection="column" className="chat-helper-button" p={5} mb={isIOSMobile ? 7 : 0} compactMode>
            <Typography className="chat-helper-text" fontSkin="body-small" textColor="subtle" mb={3}>
              Try with:
            </Typography>
            <ButtonGroup
              actionList={[
                { label: 'Help with an order', value: 'Help with an order' },
                { label: 'Help with a design project', value: 'Help with a design project' },
                { label: 'Help with checkout', value: 'Help with checkout' },
              ]}
              onSelect={onHelperButtonSelect}
              postion="start"
              type="buttons"
            />
          </FlexBox>
        )}
        <Box className={`chatWindowFooter`} compactMode id={'chatWindowFooter'}>
          <FlexBox flexDirection={'column'} style={{ width: '100%' }} compactMode>
            {selectedFilePayload && selectedFilePayload.length > 0 && (
              <Box mb={'4'}>
                <UploadAttachmentsPreview selectedFilePayload={selectedFilePayload} isLoading={uploadLoading} handleDeletePreview={handleDeletePreview} />
              </Box>
            )}
            {isIOSMobile ? <BottomFixed className="chat-footer-ios">{getFooterBox()}</BottomFixed> : getFooterBox()}
          </FlexBox>
        </Box>
      </FlexBox>
    </>
  );
});
export default ChatFooter;
